import React from 'react';
import { Button } from '../ui/button';
import { DynamicTable } from '../common/DynamicTable';
import { Pencil, Trash2 } from 'lucide-react';

export interface Fila {
  id: string;
  nome: string;
  indice?: number;
  descricao?: string;
  ativo: boolean;
  empresaId: string;
  empresaNome?: string;
  dataCriacao?: string;
  dataAtualizacao?: string;
  createdAt?: string;
  updatedAt?: string;
  status?: string;
  [key: string]: any;
}

interface FilasTableProps {
  filas: Fila[];
  loading: boolean;
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSort: (field: Extract<keyof Fila, string>, order: 'asc' | 'desc') => void;
  onSearch: (searchTerm: string) => void;
  onRowClick: (row: Fila) => void;
  onEdit: (fila: Fila) => void;
  onDelete: (fila: Fila) => void;
  canEdit: boolean;
  showEmpresaColumn?: boolean;
}

export const FilasTable: React.FC<FilasTableProps> = ({
  filas,
  loading,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  onSearch,
  onRowClick,
  onEdit,
  onDelete,
  canEdit,
  showEmpresaColumn = true,
}) => {
  const columns = [
    { 
      id: 'nome' as Extract<keyof Fila, string>, 
      label: 'Nome', 
      sortable: true 
    },
    { 
      id: 'indice' as Extract<keyof Fila, string>, 
      label: 'Índice',
      sortable: true 
    },
    { 
      id: 'descricao' as Extract<keyof Fila, string>, 
      label: 'Descrição',
      sortable: true 
    },
    ...(showEmpresaColumn ? [
      { 
        id: 'empresaNome' as Extract<keyof Fila, string>, 
        label: 'Empresa',
        sortable: true
      }
    ] : []),
    { 
      id: 'ativo' as Extract<keyof Fila, string>, 
      label: 'Status',
      align: 'center' as const,
      format: (value: boolean) => (
        <span className={`px-2 py-1 rounded-full text-xs ${value ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
          {value ? 'Ativo' : 'Inativo'}
        </span>
      )
    },
    {
      id: 'actions' as any, // 'actions' is a special column ID
      label: 'Ações',
      align: 'center' as const,
      format: (_: any, row: Fila) => (
        <div className="flex justify-center space-x-2">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={(e) => {
              e.stopPropagation();
              onEdit(row);
            }}
            className="dark:text-white dark:bg-slate-700"
            disabled={!canEdit}
          >
            <Pencil className="h-4 w-4 mr-1" />
            Editar
          </Button>
          <Button 
            size="sm" 
            variant="destructive"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(row);
            }}
            className="dark:bg-red-600 dark:hover:bg-red-700"
            disabled={!canEdit}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Excluir
          </Button>
        </div>
      )
    }
  ];

  return (
    <DynamicTable
      columns={columns}
      data={filas}
      loading={loading}
      total={total}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      onSort={onSort}
      onSearch={onSearch}
      onRowClick={onRowClick}
      mobileCards
      searchPlaceholder="Pesquisar filas..."
    />
  );
};
